import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Loader2, RefreshCw } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import HistoryMobileCard from "./HistoryMobileCard";
import HistoryPagination, { HISTORY_PAGE_SIZE } from "./HistoryPagination";

const currency = (value) =>
  typeof value === "number"
    ? `${value.toLocaleString("fr-FR")} F`
    : "—";

const modeLabel = (row) => row.order_mode || "—";

const CancellationHistory = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();
  const adminBasePath = location.pathname.startsWith("/superadmin")
    ? "/superadmin"
    : "/admin";

  const loadLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/cancellation-logs");
      if (!res.ok) throw new Error("Impossible de charger les annulations");
      const data = await res.json();
      setLogs(Array.isArray(data) ? data : []);
      setCurrentPage(1);
    } catch (err) {
      setError(err.message || "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const rows = useMemo(() => {
    const sanitizeArticle = (article, extras) => {
      if (!extras) return article;
      let cleaned = article || "";
      if (cleaned.includes(extras)) {
        cleaned = cleaned.replace(extras, "");
        cleaned = cleaned.replace(/\s*·\s*$/, "").trim();
        cleaned = cleaned.replace(/\s*·\s*$/, "").trim();
      }
      if (!cleaned) return article || "";
      return cleaned;
    };

    const mapped = (logs || []).map((log) => {
      const extras = log.extras_label || "";
      const article =
        sanitizeArticle(log.product_name, extras) || log.product_name;
      return {
        id: log.id,
        time: log.cancelled_at,
        article,
        qty: log.quantity,
        mode: modeLabel(log),
        author: log.user_name || "—",
        reason: log.cancellation_reason || "—",
        manager: log.manager_name || log.manager_code || "—",
        extras,
        estimation:
          typeof log.total_price === "number"
            ? log.total_price
            : (log.quantity || 0) * (log.unit_price || 0),
      };
    });

    // Regrouper les lignes de panier global par article/mode/raison/auteur/manager
    const grouped = mapped.reduce((acc, row) => {
      const key = [
        row.article,
        row.mode,
        row.author,
        row.manager,
        row.reason,
        row.extras,
      ].join("||");
      if (!acc[key]) {
        acc[key] = { ...row };
      } else {
        acc[key].qty += row.qty;
        acc[key].estimation += row.estimation;
      }
      return acc;
    }, {});

    return Object.values(grouped);
  }, [logs]);
  const totalPages = Math.max(1, Math.ceil(rows.length / HISTORY_PAGE_SIZE));
  const pageRows = rows.slice(
    (Math.min(currentPage, totalPages) - 1) * HISTORY_PAGE_SIZE,
    Math.min(currentPage, totalPages) * HISTORY_PAGE_SIZE,
  );

  return (
    <div className="w-full min-w-0 max-w-7xl mx-auto px-0 sm:px-4 py-4 sm:py-8 space-y-5">
      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-3 sm:flex sm:items-center">
        <button
          type="button"
          aria-label="Retour aux permissions"
          onClick={() => navigate(`${adminBasePath}/permissions`)}
          className="p-2 rounded-2xl border border-white/10 bg-white/60 text-slate-700 hover:bg-white shadow-sm dark:bg-white/5 dark:text-white dark:border-white/10 dark:hover:bg-white/10"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.25em] text-amber-400">
            Permissions
          </p>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-white">
            Historique des annulations
          </h1>
          <p className="text-sm text-slate-500 dark:text-white/60">
            Articles envoyés annulés du statut “En attente” à “Terminé”.
          </p>
        </div>
        <div className="col-span-2 sm:ml-auto sm:shrink-0">
          <button
            type="button"
            onClick={loadLogs}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl text-sm font-semibold border border-white/10 bg-white/60 hover:bg-white dark:bg-white/5 dark:text-white dark:border-white/10 dark:hover:bg-white/10"
          >
            <RefreshCw className="w-4 h-4" />
            Rafraîchir
          </button>
        </div>
      </div>

      <div className="rounded-3xl border border-white/10 bg-white/80 dark:bg-[#0b1328] dark:border-white/10 shadow-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-white/10 dark:border-white/5 flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-700 dark:text-white">
            {rows.length} annulation(s)
          </span>
        </div>

        {loading ? (
          <div className="py-10 flex items-center justify-center text-slate-500 dark:text-white/60 gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            Chargement...
          </div>
        ) : error ? (
          <div className="py-8 text-center text-red-500 text-sm">{error}</div>
        ) : rows.length === 0 ? (
          <div className="py-8 text-center text-slate-500 dark:text-white/60 text-sm">
            Aucune annulation enregistrée.
          </div>
        ) : (
          <>
          <div className="grid min-w-0 gap-3 p-3 lg:hidden">
            {pageRows.map((row) => (
              <HistoryMobileCard
                key={row.id}
                title={row.article}
                time={row.time}
                badge={`Qté annulée : ${row.qty}`}
                author={row.author}
                reason={row.reason}
                fields={[
                  ["Mode", row.mode],
                  ["Validation", row.manager],
                  ["Estimation", currency(row.estimation)],
                  ...(row.extras ? [["Extras", row.extras]] : []),
                ]}
              />
            ))}
          </div>
          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left bg-white/50 dark:bg-white/5 border-b border-white/10">
                  <th className="px-4 py-3 font-semibold text-slate-600 dark:text-white/70">
                    Heure
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-600 dark:text-white/70">
                    Article
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-600 dark:text-white/70">
                    Qté supprimée
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-600 dark:text-white/70">
                    Mode
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-600 dark:text-white/70">
                    Auteur
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-600 dark:text-white/70">
                    Motif
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-600 dark:text-white/70">
                    Validation
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-600 dark:text-white/70 text-right">
                    Estimation
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {pageRows.map((row) => (
                  <tr
                    key={row.id}
                    className="hover:bg-white/60 dark:hover:bg-white/5 transition"
                  >
                    <td className="px-4 py-3 text-slate-700 dark:text-white/80">
                      {row.time
                        ? new Date(row.time).toLocaleString("fr-FR")
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-800 dark:text-white font-semibold">
                      <div>{row.article}</div>
                      {row.extras && (
                        <div className="text-xs text-slate-500 dark:text-white/60">
                          {row.extras}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-700 dark:text-white/80">
                      {row.qty}
                    </td>
                    <td className="px-4 py-3 text-slate-700 dark:text-white/80">
                      {row.mode}
                    </td>
                    <td className="px-4 py-3 text-slate-700 dark:text-white/80">
                      {row.author}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-white/70">
                      {row.reason}
                    </td>
                    <td className="px-4 py-3 text-slate-700 dark:text-white/80">
                      {row.manager}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-800 dark:text-white font-semibold">
                      {currency(row.estimation)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <HistoryPagination
            currentPage={Math.min(currentPage, totalPages)}
            totalItems={rows.length}
            onPageChange={setCurrentPage}
          />
          </>
        )}
      </div>
    </div>
  );
};

export default CancellationHistory;
